import React from 'react';
import ReactDOM from 'react-dom';

import { t } from '@web-solutions/module-localization';

import Button from '../button/Button';
import { Text } from '../../typography/Text/Text';

import classes from './style.module.scss';

interface Props {
  /** When true, the full-screen blocking overlay is shown over the paywall. */
  visible: boolean;
  /** Poll timed out while still `pending` — show the retry affordance. */
  timedOut?: boolean;
  /** Restart polling after a timeout. */
  onRetry?: () => void;
}

/**
 * Stripe hosted-Checkout status overlay (A/B variant `useStripeCheckout`).
 *
 * A dedicated full-screen blocking loader shown on the paywall while we poll
 * `/payments/checkout/sessions/{id}/status` after the Stripe return. Unlike the
 * core `PaymentProcessingOverlay` (dark scrim, gated by `showPaymentProcessingOverlay`),
 * this one is styled like our standard loading screens — the green background +
 * green spinner — and is always shown while waiting, independent of any flag.
 */
const CheckoutProcessingOverlay: React.FC<Props> = ({ visible, timedOut, onRetry }) => {
  if (!visible || typeof document === 'undefined') {
    return null;
  }

  return ReactDOM.createPortal(
    <div className={classes.overlay} role='alert' aria-busy={!timedOut}>
      {!timedOut && (
        <svg
          className={classes.spinner}
          xmlns='http://www.w3.org/2000/svg'
          width='74px'
          height='74px'
          viewBox='0 0 100 100'
          preserveAspectRatio='xMidYMid'
        >
          <circle
            cx='50'
            cy='50'
            fill='none'
            stroke='#00CE00'
            strokeWidth='10'
            r='35'
            strokeLinecap='round'
            strokeDasharray='164.93361431346415 56.97787143782138'
          ></circle>
        </svg>
      )}

      {timedOut ? (
        <>
          <Text semibold>{t('onboarding.checkoutStatus.timeoutTitle')}</Text>
          <Text className={classes.description} size='m' medium>
            {t('onboarding.checkoutStatus.timeoutSubTitle')}
          </Text>
          <div className={classes.button}>
            <Button title={t('onboarding.checkoutStatus.retry')} onClick={onRetry} />
          </div>
        </>
      ) : (
        <>
          <Text semibold>{t('onboarding.checkoutStatus.title')}</Text>
          <Text className={classes.description} size='m' medium>
            {t('onboarding.checkoutStatus.subTitle')}
          </Text>
        </>
      )}
    </div>,
    document.body
  );
};

export default CheckoutProcessingOverlay;
